import type { CSSProperties } from "react";

/**
 * The item tree's expand / collapse motion. rc-tree wraps the nodes that appear or disappear in one
 * `.rc-tree-treenode-motion` div and hands it to rc-motion; this config animates that div's height.
 *
 * Height is the one property CSS cannot animate on its own here — the wrapper holds a variable
 * number of rows, so its open height is `auto` and there is no single child to hang the
 * `grid-template-rows: 0fr → 1fr` trick on (the pattern `KuikaCollapsePanel` uses). So the two ends
 * of the travel are measured in JS and written as inline style, and the *timing* still comes from
 * the tokens in `component-tree.scss` — no duration or curve is written here.
 *
 * rc-tree overrides `motionAppear` per wrapper (`motionType === "show"`), so an expand runs the
 * APPEAR handlers and a collapse the LEAVE ones; `enter` is kept for parity with rc-motion's
 * contract and costs nothing.
 */

/** What rc-motion reports when a step finishes: a real transition, or its own deadline firing. */
export interface ITreeMotionEndEvent {
  deadline?: boolean;
  propertyName?: string;
}

export interface ITreeCollapseMotion {
  motionName: string;
  motionDeadline: number;
  onAppearStart: () => CSSProperties;
  onAppearActive: (node: HTMLElement) => CSSProperties;
  onAppearEnd: (node: HTMLElement, event: ITreeMotionEndEvent) => boolean;
  onEnterStart: () => CSSProperties;
  onEnterActive: (node: HTMLElement) => CSSProperties;
  onEnterEnd: (node: HTMLElement, event: ITreeMotionEndEvent) => boolean;
  onLeaveStart: (node: HTMLElement) => CSSProperties;
  onLeaveActive: () => CSSProperties;
  onLeaveEnd: (node: HTMLElement, event: ITreeMotionEndEvent) => boolean;
}

export const TREE_MOTION_NAME: string = "kuika-tree-motion";

/**
 * The selected node for KuikaSelectionIndicator — unless it sits in a subtree that is folding away.
 * rc-tree keeps those rows mounted for the length of the leave motion, and the indicator, which is
 * not clipped by the wrapper, would be left standing over a fold the user has already closed.
 * `DEFAULT_SELECTION_SELECTOR` excludes a closing KuikaCollapsePanel the same way.
 */
export const TREE_SELECTED_NODE_SELECTOR: string = `.rc-tree-treenode-selected:not(.${TREE_MOTION_NAME}-leave *)`;

/** Closed: no height and no ink, so a half-open row never shows through the clip. */
const collapsedHeight = (): CSSProperties => ({ height: 0, opacity: 0 });

/** Open: exactly the rows' height, measured while the wrapper is still clipped to 0. */
const openHeight = (node: HTMLElement): CSSProperties => ({ height: node.scrollHeight, opacity: 1 });

/** Where a collapse starts from — the height the wrapper is standing at right now. */
const standingHeight = (node: HTMLElement): CSSProperties => ({ height: node.offsetHeight });

/**
 * Only the height transition (or rc-motion's deadline) ends the step. The opacity transition runs
 * alongside and finishes first; letting it end the step would cut the height off mid-travel.
 */
const heightSettled = (_node: HTMLElement, event: ITreeMotionEndEvent): boolean => {
  return event.deadline === true || event.propertyName === "height";
};

/**
 * The deadline is the safety net, not the mechanism: a `transitionend` never arrives in a background
 * tab, and without it the wrapper would stay mounted (and a collapsed subtree stay in the DOM) until
 * the tab is looked at again.
 */
const MOTION_DEADLINE_MS: number = 500;

export const TREE_COLLAPSE_MOTION: ITreeCollapseMotion = {
  motionName: TREE_MOTION_NAME,
  motionDeadline: MOTION_DEADLINE_MS,
  onAppearStart: collapsedHeight,
  onAppearActive: openHeight,
  onAppearEnd: heightSettled,
  onEnterStart: collapsedHeight,
  onEnterActive: openHeight,
  onEnterEnd: heightSettled,
  onLeaveStart: standingHeight,
  onLeaveActive: collapsedHeight,
  onLeaveEnd: heightSettled
};
